import { Controller } from "@hotwired/stimulus"
import { FetchRequest } from '@rails/request.js'

// Connects to data-controller="user-creation-form"
// Orchestrates the user edit/new form: email→accounts validation, client-role
// restrictions on the account tree, and the added/removed accounts diff tables.
// The account selection itself lives in account-tree (Stimulus outlet).
export default class extends Controller {
  static targets = ["role", "email", "emailHint", "roleHint", "platform", "accountHistory"]
  static outlets = ["account-tree"]
  static values = {
    initialAccounts: Array,
    accountsUpdated: { type: Boolean, default: false },
    removedAccounts: Array,
    addedAccounts: Array,
    validateUrl: String,
    userEmail: String,
    persisted: { type: Boolean, default: false }
  }

  connect() {
    this.element.addEventListener("account-tree:change", (e) => this.onAccountTreeChange(e))

    if (this.hasAccountTreeOutlet) {
      if (!this.userEmailValue) this.accountTreeOutlet.setDisabled(true)
      if (this.roleTarget.value === "client") {
        const initialIds = this.initialAccountsValue.map((a) => a.id)
        this.accountTreeOutlet.restrictToRelated(initialIds)
      }
    }
  }

  userEmailChanged() {
    this.userEmailValue = this.emailTarget.value
    if (!this.userEmailValue) {
      this.emailHintTarget.classList.remove("hidden")
      this.accountTreeOutlet?.setDisabled(true)
    } else {
      this.emailHintTarget.classList.add("hidden")
      this.accountTreeOutlet?.setDisabled(false)
    }
  }

  toggleRoleField() {
    const role = this.roleTarget.value
    const isProviderPartner = role === "provider_partner"

    // Reveal the provider-scope field and toggle its inputs' disabled state.
    // Disabled inputs aren't submitted, so a non-partner never posts a scope.
    // querySelectorAll over the fields (not a single `select`) so this works for
    // the checkbox group and won't throw if the markup changes.
    this.platformTarget.classList.toggle("hidden", !isProviderPartner)
    this.platformTarget
      .querySelectorAll("input, select")
      .forEach((field) => { field.disabled = !isProviderPartner })

    if (role === "client") {
      this.roleHintTarget.classList.remove("hidden")
      const initialIds = this.initialAccountsValue.map((a) => a.id)
      this.accountTreeOutlet?.restrictToRelated(initialIds)
    } else {
      this.roleHintTarget.classList.add("hidden")
      this.accountTreeOutlet?.clearRestrictions()
    }
  }

  onSubmit(e) {
    // Only warn when editing an existing user's access — a brand-new user has
    // nothing to "change", so let its submission flow through normally.
    if (!this.accountsUpdatedValue || !this.persistedValue) return

    e.preventDefault()
    const baseMsg = "You have changed the account access for this user. Please review the tables before you continue.\n\nAre you sure you want to continue?"
    const conflictMsg = "You have changed the account access for this user AND the email conflicts with users already on one of the new accounts.\n\nAre you sure you want to continue?"
    const message = this.addedAccountsValue.some((a) => a.valid === false) ? conflictMsg : baseMsg

    if (confirm(message)) e.target.submit()
  }

  // ─── React to account-tree changes ────────────────────────────────────

  onAccountTreeChange(event) {
    const { added, removed, selectedIds } = event.detail

    const addedRecords = added.map((id) => ({id, name: this.nameForId(id), valid: null}))
    const removedRecords = removed.map((id) => ({id, name: this.nameForId(id)}))

    this.accountsUpdatedValue = added.length > 0 || removed.length > 0
    this.accountHistoryTarget.classList.toggle("hidden", !this.accountsUpdatedValue)

    this.removedAccountsValue = removedRecords

    if (this.userEmailValue && added.length > 0) {
      this.validateUserEmailAgainstAccounts(addedRecords)
        .then((validated) => { this.addedAccountsValue = validated })
        .catch(() => { this.addedAccountsValue = addedRecords })
    } else {
      this.addedAccountsValue = addedRecords
    }

    if (this.roleTarget.value === "client") {
      this.accountTreeOutlet?.restrictToRelated(selectedIds)
    }
  }

  nameForId(id) {
    return this.accountTreeOutlet?.accountNameFor(id) ?? `Account ${id}`
  }

  removedAccountsValueChanged(newValue) {
    if (!this.hasAccountHistoryTarget) return
    const body = this.accountHistoryTarget.querySelector("#removed-accounts tbody")
    if (!body) return
    const rows = newValue.length > 0
      ? newValue.map((a) => this.tableRow(a.name))
      : [this.tableRow("No accounts to display")]
    body.replaceChildren(...rows.filter(Boolean))
  }

  addedAccountsValueChanged(newValue) {
    if (!this.hasAccountHistoryTarget) return
    const body = this.accountHistoryTarget.querySelector("#added-accounts tbody")
    if (!body) return
    const rows = newValue.length > 0
      ? newValue.map((a) => this.tableRow(a.name, a.valid))
      : [this.tableRow("No accounts to display")]
    body.replaceChildren(...rows.filter(Boolean))
  }

  // Build the row with createElement/textContent (never innerHTML) so account
  // names can't inject markup.
  tableRow(accountName, emailValid = null) {
    if (accountName == null) return null
    const tr = document.createElement("tr")
    if (emailValid === true) tr.className = "bg-sg-success-light"
    else if (emailValid === false) tr.className = "bg-sg-error-light"

    const nameCell = document.createElement("td")
    nameCell.className = "px-4 py-2 text-sm text-sg-gray-700"
    nameCell.textContent = accountName
    tr.appendChild(nameCell)

    if (emailValid !== null) {
      const statusCell = document.createElement("td")
      statusCell.className = "px-4 py-2 text-sm text-sg-gray-700"
      statusCell.textContent = emailValid ? "Valid" : "Address conflict detected"
      tr.appendChild(statusCell)
    }
    return tr
  }

  async validateUserEmailAgainstAccounts(addedRecords) {
    const ids = addedRecords.map((a) => a.id)
    const request = new FetchRequest("post", this.validateUrlValue, {
      body: {email: this.emailTarget.value, account_ids: ids}
    })
    const response = await request.perform()
    if (!response.ok) return addedRecords
    return await response.json
  }
}
